import { useState } from "react";
import type { SDKVersion, SDKVersionRange, SDKVersionRangeBound } from "../../types/experiment";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { X, Plus } from "lucide-react";

interface SDKVersionEditorProps {
  label: string;
  value?: SDKVersion;
  onChange: (value: SDKVersion | undefined) => void;
  className?: string;
}

function VersionBoundInput({
  value,
  onChange,
  label,
}: {
  value?: SDKVersionRangeBound;
  onChange: (v: SDKVersionRangeBound | undefined) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-[10px] text-overlay1 w-6 shrink-0">{label}</Label>
      <Input
        type="number"
        placeholder="version"
        value={value?.version?.version ?? ""}
        onChange={(e) => {
          const version = e.target.value ? parseInt(e.target.value) : undefined;
          if (version === undefined) {
            onChange(undefined);
          } else {
            onChange({ version: { version }, inclusive: value?.inclusive ?? true });
          }
        }}
        className="h-8 text-xs font-mono"
      />
      <label className="flex items-center gap-1.5 text-[10px] text-overlay0 cursor-pointer shrink-0">
        <Checkbox
          checked={value?.inclusive ?? true}
          onCheckedChange={(checked) =>
            onChange({
              version: value?.version,
              inclusive: !!checked,
            })
          }
        />
        incl
      </label>
    </div>
  );
}

function SdkRangeRow({
  value,
  onChange,
  onRemove,
}: {
  value: SDKVersionRange;
  onChange: (v: SDKVersionRange) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <VersionBoundInput
        value={value.lower_bound}
        onChange={(v) => onChange({ ...value, lower_bound: v })}
        label="From"
      />
      <span className="text-overlay0 text-[10px]">to</span>
      <VersionBoundInput
        value={value.upper_bound}
        onChange={(v) => onChange({ ...value, upper_bound: v })}
        label="To"
      />
      <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7 shrink-0">
        <X className="h-3 w-3 text-red" />
      </Button>
    </div>
  );
}

export function SDKVersionEditor({
  label,
  value,
  onChange,
  className,
}: SDKVersionEditorProps) {
  const sdk: SDKVersion = value ?? { ranges: [], work_around_pyoto_bug: false };

  const addRange = () => onChange({ ...sdk, ranges: [...sdk.ranges, {}] });
  const updateRange = (i: number, range: SDKVersionRange) => {
    const ranges = [...sdk.ranges];
    ranges[i] = range;
    onChange({ ...sdk, ranges });
  };
  const removeRange = (i: number) =>
    onChange({ ...sdk, ranges: sdk.ranges.filter((_, j) => j !== i) });

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-medium text-subtext1">{label}</Label>
        <label className="flex items-center gap-1.5 text-[10px] text-overlay0 cursor-pointer">
          <Checkbox
            checked={sdk.work_around_pyoto_bug}
            onCheckedChange={(v) => onChange({ ...sdk, work_around_pyoto_bug: !!v })}
          />
          pyoto bug
        </label>
      </div>
      <div className="space-y-1.5">
        {sdk.ranges.map((range, i) => (
          <SdkRangeRow
            key={i}
            value={range}
            onChange={(r) => updateRange(i, r)}
            onRemove={() => removeRange(i)}
          />
        ))}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={addRange} className="mt-2 h-7 text-xs w-full border border-dashed border-surface1 hover:border-mauve/50">
        <Plus className="h-3 w-3 mr-1" />
        Add Range
      </Button>
    </div>
  );
}
